/* BD Vehicle Bar — page-level product filtering (design granularity, not variants).
   Grades each variant's real parts (bdw.stock) with the shared core grader, hides
   non-fitting product cards, and badges the rest. Vehicle state is shared with the
   finder page and the PDP buy box via BDFitCore's storage. */
(() => {
  const C = window.BDFitCore;
  const root = document.querySelector("[data-bd-vbar]");
  if (!root) return;
  const KEY = C.resolveKey(window.BDVB_KEY);
  const PRODUCTS = JSON.parse(root.parentElement.querySelector("[data-vbar-products]")?.textContent
    ?? document.querySelector("[data-vbar-products]").textContent);
  const els = {
    state: root.querySelector("[data-vbar-state]"),
    picker: root.querySelector("[data-vbar-picker]"),
    summary: root.querySelector("[data-vbar-summary]"),
  };
  // Part-level grading — the SAME grader as the finder page and the PDP buy box,
  // so a design badged here can never contradict its product page. (The earlier
  // envelope shortcut produced cross-part false positives: pattern satisfied by
  // one part, offset by another, no physical wheel satisfying both.)
  function productVerdict(p, v) {
    let graded = false, setBest = null;
    // catalog designs: a design only "fits" when BOTH axles have a workable
    // size — fronts with no possible rears aren't a set you can put on a car
    const axBest = { front: 3, rear: 3 };            // C.RANK scale, 3 = nothing fits
    const axStock = { front: false, rear: false };
    // Off-Road series skips the offset check (handle/tags emitted by the section)
    const opts = { ignoreOffset: C.isOffroad(p) };
    for (const e of p.variants) {
      // set listings (clearance/overstock): complementary axle positions,
      // every position must fit — in-stockness is the LISTING's own quantity
      if (e.set_stock && e.set_stock.length) {
        graded = true;
        const g = C.gradeSetPositions(e.set_stock, v, opts);
        if (g.verdict === "no") continue;
        if ((g.verdict === "ok" || g.verdict === "warn") && e.qty > 0) return "stock";
        setBest = "built";
        continue;
      }
      const size = C.parseSize(e.size);
      if (!size) continue;
      if (e.stock && e.stock.length) graded = true;
      for (const axle of ["front", "rear"]) {
        const env = (v.axles && v.axles[axle]) || v;
        const g = C.gradeParts(e.stock, size, env, v.patterns, v.bore, opts);
        if (g.verdict === "no") continue;
        axBest[axle] = Math.min(axBest[axle], C.RANK[g.verdict]);
        // any fitting part with inventory — drilled OR blank — is in stock
        if (g.anyStock) axStock[axle] = true;
      }
    }
    if (setBest) return setBest;
    if (axBest.front < 3 && axBest.rear < 3)
      return axStock.front && axStock.rear ? "stock" : "built";
    // no fitment data at all: stay neutral — visible, unbadged, never hidden
    if (!graded) return "unknown";
    return "no";
  }

  function cardsByProductId() {
    const map = new Map();
    document.querySelectorAll("product-card[data-product-id]").forEach((card) => {
      const id = +card.dataset.productId;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(card);
    });
    return map;
  }

  const wrapperOf = (card) => card.closest(".resource-list__item") || card.closest("li") || card;

  let selfMutating = false;   // our own DOM writes must not re-trigger the observer
  function applyFilter() {
    selfMutating = true;
    try { applyFilterInner(); } finally {
      requestAnimationFrame(() => requestAnimationFrame(() => { selfMutating = false; }));
    }
  }

  function applyFilterInner() {
    const saved = C.loadVehicle();
    const v = preview || (!pendingPick && saved && saved.vehicle) || null;
    const cards = cardsByProductId();
    document.querySelectorAll(".bdvb-badge").forEach((b) => b.remove());
    const ORDER = { stock: 0, built: 1 };
    const byContainer = new Map();   // container -> [{wrap, rank, idx}]
    let shown = 0, total = 0;
    for (const p of PRODUCTS) {
      const els2 = cards.get(p.id) || [];
      if (!els2.length) continue;
      total++;
      const verdict = v ? productVerdict(p, v) : null;
      const hide = verdict === "no";
      if (!hide) shown++;   // "unknown" (no data) stays visible and counted
      for (const card of els2) {
        const wrap = wrapperOf(card);
        if (wrap.dataset.bdvbIdx === undefined) wrap.dataset.bdvbIdx = [...wrap.parentElement.children].indexOf(wrap);
        wrap.style.display = hide ? "none" : "";
        if (!byContainer.has(wrap.parentElement)) byContainer.set(wrap.parentElement, []);
        byContainer.get(wrap.parentElement).push({ wrap, rank: verdict ? ORDER[verdict] ?? 9 : 0, idx: +wrap.dataset.bdvbIdx });
        if (verdict === "stock" || verdict === "built") {
          card.insertAdjacentHTML("afterbegin",
            `<span class="bdvb-badge bdvb-badge--${verdict}">${verdict === "stock" ? "Fits · in stock" : "Fits · built to order"}</span>`);
        }
      }
    }
    // reposition: fitting wheels first (in stock, then built to order); original order without a vehicle
    for (const [container, items] of byContainer) {
      items.sort((a, b) => a.rank - b.rank || a.idx - b.idx);
      const target = items.map((i) => i.wrap);
      const current = [...container.children].filter((c) => target.includes(c));
      if (target.some((w, i) => w !== current[i])) container.prepend(...target);
    }
    renderSummary(v, shown, total);
  }

  function renderSummary(v, shown, total) {
    if (!v) { els.summary.innerHTML = ""; return; }
    els.summary.innerHTML = `<span><b>${shown}</b> of ${total} designs fit your <b>${v.label}</b></span>
      <button type="button" class="bdbb-link" data-vbar-clear>clear</button>`;
    els.summary.querySelector("[data-vbar-clear]").onclick = () => { preview = null; pendingPick = false; C.clearVehicle(); C.writeVehicleToUrl(null); renderState(); applyFilter(); };
  }

  function renderState() {
    const saved = C.loadVehicle();
    if (saved && saved.vehicle) {
      els.picker.hidden = true;
      els.state.innerHTML = `<span class="bdvb-current">${saved.vehicle.label}</span>
        <button type="button" class="bdbb-link" data-vbar-change>Change vehicle</button>`;
      els.state.querySelector("[data-vbar-change]").onclick = () => { els.picker.hidden = false; mount(); };
    } else {
      els.state.innerHTML = "";
      els.picker.hidden = false;
      mount();
    }
  }

  let mounted = false;
  let pendingPick = false;   // mid-repick: suspend filtering without dropping the saved vehicle
  let preview = null;        // model picked, trim pending: filter on the all-trims union
  function mount() {
    if (mounted) return;
    if (!KEY) { els.picker.innerHTML = `<span class="bdvb-current">Fitment lookup not configured.</span>`; return; }
    mounted = true;
    C.mountPicker(els.picker, { api: C.makeApi(KEY),
      onVehicle: () => { mounted = false; pendingPick = false; preview = null; C.writeVehicleToUrl(C.loadVehicle()); renderState(); applyFilter(); },
      onProvisional: (v) => { preview = v; applyFilter(); },
      onPartial: () => { pendingPick = true; preview = null; applyFilter(); } });
  }

  renderState();
  applyFilter();
  // ad/share landings: ?make=&model=&year= selects the vehicle on arrival
  if (KEY) {
    C.resolveVehicleFromUrl(C.makeApi(KEY)).then((r) => {
      if (r) { renderState(); applyFilter(); }
    }).catch(() => {});
  }
  // Horizon hydrates/morphs sections client-side, which can replace both the grid
  // and this bar's markup after our first render — re-render whenever that happens.
  let scheduled = false;
  new MutationObserver((muts) => {
    if (selfMutating) return;
    const relevant = muts.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1
      && (n.matches?.("product-card, [data-vbar-state]") || n.querySelector?.("product-card, [data-vbar-state]"))));
    if (!relevant || scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      const state = root.isConnected ? els.state : document.querySelector("[data-vbar-state]");
      if (state && state !== els.state) { els.state = state; els.picker = document.querySelector("[data-vbar-picker]"); els.summary = document.querySelector("[data-vbar-summary]"); mounted = false; }
      if (els.state && !els.state.childNodes.length) renderState();
      applyFilter();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
